import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import type {
  ReflexPanelProposal,
  TissueCypherSignOutProposal,
} from "../../services/reflexPanels.service";
import ReflexPanelProposalOption from "./ReflexPanelProposalOption";
import TissueCypherProposalOption from "./TissueCypherProposalOption";

interface ReflexPanelDecisionModalProps {
  opened: boolean;
  proposals: ReflexPanelProposal[];
  tissueCypherProposals?: TissueCypherSignOutProposal[];
  submitting: boolean;
  onCancel: () => void;
  onContinue: (acceptedProposalKeys: string[]) => void;
}

export default function ReflexPanelDecisionModal({
  opened,
  proposals,
  tissueCypherProposals = [],
  submitting,
  onCancel,
  onContinue,
}: ReflexPanelDecisionModalProps) {
  const [acceptedKeys, setAcceptedKeys] = useState<string[]>(() =>
    [
      ...proposals,
      ...tissueCypherProposals.filter(
        (proposal) => !proposal.requiresExplicitSpecimenSelection
      ),
    ].map((proposal) => proposal.proposalKey)
  );

  const handleToggle = useCallback((proposalKey: string, checked: boolean) => {
    setAcceptedKeys((previous) =>
      checked
        ? previous.includes(proposalKey)
          ? previous
          : [...previous, proposalKey]
        : previous.filter((key) => key !== proposalKey)
    );
  }, []);
  const handleContinue = useCallback(() => {
    onContinue(acceptedKeys);
  }, [acceptedKeys, onContinue]);
  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title="Panel recommendations"
      size="lg"
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
    >
      <Stack gap="md">
        <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
          Accepted panels create additional-stain work and cannot be removed
          once processing begins. Clear a checkbox to sign out without ordering
          that panel.
        </Alert>
        {tissueCypherProposals.some(
          (proposal) => proposal.requiresExplicitSpecimenSelection
        ) && (
          <Text size="sm">
            This addendum ICD-10 code applies to the case. Select each specimen
            that needs Tissue Cypher for this code; case-level targets are not
            selected automatically.
          </Text>
        )}
        {proposals.map((proposal) => (
          <ReflexPanelProposalOption
            key={proposal.proposalKey}
            proposal={proposal}
            checked={acceptedKeys.includes(proposal.proposalKey)}
            disabled={submitting}
            onToggle={handleToggle}
            distinguishFromTissueCypher={tissueCypherProposals.length > 0}
          />
        ))}
        {tissueCypherProposals.map((proposal) => (
          <TissueCypherProposalOption
            key={proposal.proposalKey}
            proposal={proposal}
            checked={acceptedKeys.includes(proposal.proposalKey)}
            disabled={submitting}
            onToggle={handleToggle}
          />
        ))}
        <Group justify="space-between">
          <Button
            variant="default"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel sign-out
          </Button>
          <Button onClick={handleContinue} loading={submitting}>
            {acceptedKeys.length > 0
              ? "Order selected and continue"
              : "Continue without ordering"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
